import React, { useState } from "react";
import styled from "styled-components";
import { Layout } from "../components/layout/Layout";
import { MediaGrid } from "../components/media/MediaGrid";
import { LoadingSpinner } from "../components/common/LoadingSpinner";
import { Search as SearchIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { tmdbApi } from "../services/tmdb";
import { useDebounce } from "../hooks/useDebounce";
import { SearchSuggestions } from "../components/search/SearchSuggestions";
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";
import { useNavigate } from "react-router-dom";

const SearchContainer = styled.div`
  display: flex;
  gap: var(--spacing-6);
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SearchHeader = styled.div`
  margin-bottom: var(--spacing-4);
  position: relative;
  z-index: 10;
  
  @media (max-width: 768px) {
    z-index: 1000;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: var(--spacing-3);
  color: var(--color-text-primary);
`;

const SearchInputWrapper = styled.div`
  position: relative;
  max-width: 600px;
  margin-bottom: var(--spacing-4);
  z-index: 100;
  isolation: isolate;
  
  @media (max-width: 768px) {
    max-width: 100%;
    z-index: 9999;
    isolation: isolate;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: var(--spacing-3) var(--spacing-3) var(--spacing-3)
    calc(var(--spacing-3) * 2 + 24px);
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: var(--color-text-primary);
  font-size: 1.1rem;
  transition: all 0.2s ease;
  -webkit-appearance: none;
  appearance: none;
  position: relative;
  z-index: 10;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    background: rgba(255, 255, 255, 0.15);
  }

  &::placeholder {
    color: var(--color-text-secondary);
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 1rem 1rem 1rem calc(1rem * 2 + 24px);
    min-height: 48px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    pointer-events: auto;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  left: var(--spacing-3);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-secondary);
  pointer-events: none;
  z-index: 1;
`;

const NoResults = styled.div`
  text-align: center;
  padding: var(--spacing-6);
  color: var(--color-text-secondary);
`;

const Search = () => {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  
  useScrollToTopOnMount();
  
  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (query.trim().length > 0) {
      navigate(`/browse?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleWrapperClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const { data: searchResults, isLoading } = useQuery({
    queryKey: ["search", debouncedQuery, currentPage],
    queryFn: () => tmdbApi.search(debouncedQuery, currentPage),
    enabled: !!debouncedQuery,
  });

  const filteredResults = searchResults?.results?.filter(
    (item: any) => item.media_type === "movie" || item.media_type === "tv",
  );

  return (
    <Layout>
      <SearchContainer>
        <MainContent>
          <SearchHeader>
            <Title>Rechercher</Title>
            <SearchInputWrapper onClick={handleWrapperClick}>
              <IconWrapper>
                <SearchIcon size={24} />
              </IconWrapper>
              <SearchInput
                ref={inputRef}
                type="text"
                placeholder="Rechercher des films ou séries..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                autoComplete="off"
                inputMode="search"
              />
            </SearchInputWrapper>
          </SearchHeader>

          {isLoading ? (
            <LoadingSpinner />
          ) : filteredResults?.length > 0 ? (
            <>
              <MediaGrid items={filteredResults} type="movie" />
            </>
          ) : query && !isLoading ? (
            <NoResults>Aucun résultat trouvé</NoResults>
          ) : (
            <>
              <SearchSuggestions />
            </>
          )}
        </MainContent>
      </SearchContainer>
    </Layout>
  );
};

export default Search;
